import { JSXElementConstructor, ReactElement, useMemo, useRef } from "react";
import { flattenToTimeline, parseStringToArray } from "../parser/nestedSubdivisionParser";

type PrimitiveCommandCallback = (...args: any) =>
  void |
  string |
  React.ReactElement |
  Promise<void | string | React.ReactElement>

type CommandCallback = (...args: any) =>
  void |
  string |
  React.ReactElement |
  Promise<void | string | React.ReactElement> |
  Promise<string | void | (() => Promise<void>) | ReactElement<any, string | JSXElementConstructor<any>>>;
export interface CommandMap {
  [key: string]: CommandCallback;
}

interface PrimitiveCommandMap {
  [key: string]: PrimitiveCommandCallback;
}

interface Cycle {
  interval: NodeJS.Timeout,
  command: string;
}

type NestedCycles = Map<number, {
  interval: NodeJS.Timeout;
  timeouts: NodeJS.Timeout[];
  command: string;
}>

let nextId = 0;

export const useCommandMap = (
  videoElement: HTMLVideoElement | null,
  setSrc: React.Dispatch<React.SetStateAction<string>>
) => {
  const intervalList = useRef<Cycle[]>([]);
  const nestedCycles = useRef<NestedCycles>(new Map());

  const consolidatedCommandMap = useMemo(() => {
    const commandMap: PrimitiveCommandMap = {
      hello: () => {
        return `hello there! type 'man' to get a list of commands`;
      },
      man: () => {
        return `
                ___List of commands___
                testStreams - lists test streams
                play - plays video
                pause - pauses video
                volume [0 - 1] - sets volume
                speed [0 - 5] - sets playbackrate (pitch preservation is disabled)
                load [url] - loads a video or hls playlist
                seekTo [seconds] - seeks video to that time in seconds
                seekBy [seconds] - seeks video forward by seconds
                after [timeout] [command] - fires a command after timeout (in ms)
                every [interval] [command] - fires a command after interval (in ms)
                pSeek [interval] [timestamps] - cycles through timestamps in a list every interval (in ms)
                  usage: pSeek 5000 "400 20 54 71"
                pVol [interval] [volumes] - cycles through volumes in a list every interval (in ms)
                  usage: pVol 200 "0.3 0.1 0 0.97 0.8 0.2"
                pSpeed [interval] [speeds] - cycles through playback speeds in a list every interval (in ms) 
                  usage: pSpeed 1000 "0.5 0.3 2 1.4"
                showCycles shows all ongoing cyclical processes with id
                kill [id] - kills a cyclical process associated with the id
                  usage: kill 0
                killAll - kills all cyclical processes
            `;
      },
      play: async () => {
        await videoElement?.play();
        return `playing`
      },
      pause: () => {
        videoElement?.pause();
      },
      volume: (volume?: number) => {
        if (!videoElement) return;
        if (volume === undefined) return `${videoElement.volume}`;
        videoElement.volume = volume;
        return `volume: ${videoElement.volume}`;
      },
      speed: (playbackRate?: number) => {
        if (!videoElement) return;
        if (playbackRate === undefined) return `${videoElement.playbackRate}`;
        videoElement.preservesPitch = false;
        videoElement.playbackRate = playbackRate;
        return `volume: ${videoElement.playbackRate}`;
      },
      seekTo: (time: number) => {
        if (!videoElement) return;
        videoElement.currentTime = time;
      },
      seekBy: (time: string) => {
        if (!videoElement) return;
        const currentTime = videoElement.currentTime;
        videoElement.currentTime = currentTime + Number(time);
      },
      seekRand: () => {
        if (!videoElement) return;
        videoElement.currentTime = Math.random() * videoElement.duration;
      },
      load: (newSrc: string) => {
        setSrc(newSrc);
        return `loaded ${newSrc}`;
      },
      showCycles: () => {
        const list = intervalList.current.map((interval, index) => `${index} - ${interval.command}`);
        return list.join('\n');
      },
      kill: (id: string) => {
        const idNum = Number(id);
        const cycle = nestedCycles.current.get(idNum);

        if (!cycle) {
          // Legacy interval list
          // TODO get rid of this entirely
          if (idNum < 0 || idNum >= intervalList.current.length) return;
          const { interval } = intervalList.current[idNum];
          if (interval) clearInterval(interval);
          intervalList.current.splice(idNum, 1);
          return;
        };

        clearInterval(cycle.interval);
        cycle.timeouts.forEach(t => clearTimeout(t));
        nestedCycles.current.delete(idNum);
      },
      killAll: () => {
        intervalList.current.forEach(i => clearInterval(i.interval));
        intervalList.current = [];
        Array.from(nestedCycles.current.keys()).forEach(key => {
          const cycle = nestedCycles.current.get(key);
          clearInterval(cycle?.interval);
          cycle?.timeouts.forEach(t => clearTimeout(t));
          nestedCycles.current.delete(key);
        })
      },
      reset: () => {
        intervalList.current.forEach(i => clearInterval(i.interval));
        intervalList.current = [];
        Array.from(nestedCycles.current.keys()).forEach(key => {
          const cycle = nestedCycles.current.get(key);
          clearInterval(cycle?.interval);
          cycle?.timeouts.forEach(t => clearTimeout(t));
          nestedCycles.current.delete(key);
        });
        if (!videoElement) return;
        videoElement.pause();
        videoElement.currentTime = 0;
        videoElement.volume = 1;
        videoElement.playbackRate = 1;
      }
    };

    const deferredCommandMap = {
      after: async (timeout: number, command: keyof CommandMap, ...args: any) => {
        const commandFn = (commandMap as PrimitiveCommandMap)[command];
        await new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve();
          }, timeout);
        });
        const result = await commandFn(...args);
        return result;
      },
      every: (intervalMs: number, command: keyof CommandMap, ...args: any) => {
        const commandFn = (commandMap as PrimitiveCommandMap)[command];
        if (!commandFn) return `${command} is invalid`;
        const interval = setInterval(() => commandFn(args), intervalMs);
        intervalList.current.push({
          interval,
          command: `${command} ${args.join(' ')}`
        });
        return `id: ${intervalList.current.length - 1}`
      },
      evalPatternedExpression: (intervalMs: number, patternString: string, command: PrimitiveCommandCallback, name: string) => {
        try {
          const pattern = patternString.split(' ').map((i) => Number(i));
          let index = 0;
          const interval = setInterval(() => {
            console.log(pattern[index]);
            command(pattern[index]);
            index++;

            if (index >= pattern.length) index = 0;
          }, intervalMs);
          intervalList.current.push({
            interval,
            command: `${name} ${patternString}`
          });
          return `id: ${intervalList.current.length - 1}`
        } catch (e: any) {
          return `Pattern error: ${e.message}`;
        }
      },
      evaluateNestedPatternedExpression: (intervalMs: number, patternString: string, command: PrimitiveCommandCallback, name: string) => {
        const timeouts: NodeJS.Timeout[] = [];
        try {
          const tree = parseStringToArray(patternString);
          const cycleDuration = intervalMs * tree.length;
          const timeline = flattenToTimeline(tree, cycleDuration);

          const id = nextId++;

          const runCycle = () => {
            timeouts.forEach(t => clearTimeout(t));
            timeouts.length = 0;

            timeline.forEach(([value, offset]) => {
              const timeout = setTimeout(() => {
                command(value);
              }, offset);
              timeouts.push(timeout);
            })
          }

          runCycle();
          // Add 1s so that timeouts scheduled at the very end can fire before cycle reset
          const mainInterval = setInterval(runCycle, cycleDuration + 1);

          nestedCycles.current.set(id, {
            interval: mainInterval,
            timeouts,
            command: `${name} ${patternString}`
          });

          return `id: ${id}`;
        } catch (e: any) {
          // Need to clear timeouts here
          timeouts.forEach(t => clearTimeout(t));
          timeouts.length = 0;
          return `Pattern error: ${e.message}`;
        }
      }
    }

    const patternCommands = {
      pSeek: (intervalMs: number, patternString: string) => {
        return deferredCommandMap.evalPatternedExpression(intervalMs, patternString, commandMap.seekTo, 'pSeek');
      },
      pVol: (intervalMs: number, patternString: string) => {
        return deferredCommandMap.evalPatternedExpression(intervalMs, patternString, commandMap.volume, 'pVol');
      },
      pSpeed: (intervalMs: number, patternString: string) => {
        return deferredCommandMap.evalPatternedExpression(intervalMs, patternString, commandMap.speed, 'pSpeed');
      },
      pNestedSeek: (intervalMs: number, patternString: string) => {
        return deferredCommandMap.evaluateNestedPatternedExpression(intervalMs, patternString, commandMap.seekTo, 'pSeek');
      }
    }

    const debugCommands = {
      testPattern: (patternString: string) => {
        return JSON.stringify(parseStringToArray(patternString));
      },
      testTimelineOffsets: (intervalMs: number, patternString: string) => {
        const array = parseStringToArray(patternString);
        const cycleDuration = intervalMs * array.length;
        const offsetList = flattenToTimeline(array, cycleDuration);
        return JSON.stringify(offsetList);
      }
    }

    const helpCommands = {
      testStreams: () => `
        Skate - https://sample.vodobox.net/skate_phantom_flex_4k/skate_phantom_flex_4k.m3u8
        BBB - https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8
        Car for a grand - https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/WhatCarCanYouGetForAGrand.mp4
      `
    }

    return {
      ...helpCommands,
      ...commandMap,
      ...deferredCommandMap,
      ...patternCommands,
      ...debugCommands
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoElement, setSrc, intervalList]);

  return consolidatedCommandMap;
};